<template>
  <div class="login-overlay" @click.self="onCancel">
    <div class="login-card">

      <div class="login-header">
        <h2>{{ variant === 'upload' ? t('uploadPwTitle') : t('passwordOfferTitle') }}</h2>
        <button class="close-btn" @click="onCancel">&#10005;</button>
      </div>

      <div class="form">
        <p class="info-message">
          {{ variant === 'upload' ? t('uploadPwBody') : t('passwordOfferBody') }}
        </p>

        <input
            v-model="password"
            type="password"
            :placeholder="t('newPassword')"
            @keyup.enter="submit"
        />
        <input
            v-model="passwordRepeat"
            type="password"
            :placeholder="t('repeatNewPassword')"
            @keyup.enter="submit"
        />

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <button :disabled="!canSubmit || busy" @click="submit">
          {{ variant === 'upload' ? t('setPasswordAndUpload') : t('setPassword') }}
        </button>

        <button class="secondary-btn" :disabled="busy" @click="onCancel">
          {{ t('later') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from '../i18n.js'

const MIN_PASSWORD_LENGTH = 10

export default {
  name: 'PasswordSetupDialog',

  props: {
    service: { type: Object, required: true },
    variant: { type: String, default: 'optional' } // 'optional' | 'upload'
  },

  emits: ['done', 'cancel'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      password: '',
      passwordRepeat: '',
      busy: false,
      errorMessage: ''
    }
  },

  computed: {
    canSubmit() {
      return this.password.length >= MIN_PASSWORD_LENGTH
          && this.password === this.passwordRepeat
    }
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.onCancel()
    },

    async submit() {
      if (this.busy) return
      if (this.password.length < MIN_PASSWORD_LENGTH) {
        this.errorMessage = this.t('passwordTooShort10')
        return
      }
      if (this.password !== this.passwordRepeat) {
        this.errorMessage = this.t('passwordMismatch')
        return
      }
      this.busy = true
      this.errorMessage = ''
      try {
        await this.service.setPassword(this.password)
        this.$emit('done')
      } catch (e) {
        this.errorMessage = this.t('passwordChangeFailed')
        this.busy = false
      }
    },

    onCancel() {
      if (this.busy) return
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 130;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 18px; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.login-card {
  width: min(420px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-message {
  margin: 0;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.45;
}

input {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
}

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}
button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}
.secondary-btn:hover:not(:disabled) { background: #e4e8ee; }

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
